import React, { useState, useEffect } from 'react';
import { useLocation, Outlet, useNavigate } from 'react-router-dom';
import { setPageTitle } from '../../utils/pageTitle';
import { 
  LayoutGrid, 
  ChevronLeft, 
  Home,
  Settings,
  Menu,
  X,
  Shield,
  Database
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useIsAdmin } from '../../hooks/useIsAdmin';
import { employeeNamesService } from '../../services/employeeNames';
import { UserInfoSection } from '../layout/UserInfoSection';
import { usePMT3Selection } from '../../context/PMT3SelectionContext';
import { useDarkMode } from '../../context/DarkModeContext';
import '../../styles/dialog-select.css';
import '../../styles/pmt3-card-styles.css';
import '../../styles/pmt2-select-override.css';
import '../../styles/select-padding.css';
import KeyboardNavigableSelect from '../ui/KeyboardNavigableSelect';
import { ASINReviewForm } from '../pmt3/ASINReviewForm';
import { Input } from '../ui/input';
import { pmt2Service } from '../../services/pmt2Service';

const PMT2Layout: React.FC = () => {
  const [displayName, setDisplayName] = useState<string>('');
  const location = useLocation();
  const navigate = useNavigate();
  const { userEmail } = useAuth();
  const { isDarkMode } = useDarkMode();
  const [isCollapsed, setIsCollapsed] = useState(window.innerWidth < 1024);
  const [isFullScreen, setIsFullScreen] = useState(window.innerWidth >= 1024);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  // Reverse ASIN lookup state
  const [pasteAsin, setPasteAsin] = useState<string>('');
  const [asinValidationStatus, setAsinValidationStatus] = useState<'idle' | 'validating' | 'valid' | 'invalid'>('idle');
  const [lookupTimeout, setLookupTimeout] = useState<NodeJS.Timeout | null>(null);
  
  // Use the PMT3Selection context
  const { 
    companies, 
    selectedCompany, 
    setSelectedCompany, 
    products, 
    selectedProduct, 
    setSelectedProduct 
  } = usePMT3Selection();

  useEffect(() => {
    const loadDisplayName = async () => {
      if (userEmail) {
        const name = await employeeNamesService.getNameFromEmail(userEmail);
        if (name) {
          setDisplayName(name);
        }
      }
    };
    loadDisplayName();
  }, [userEmail]);

  // Handle window resize for responsive layout
  useEffect(() => {
    const handleResize = () => {
      const width = window.innerWidth;
      setWindowWidth(width);
      setIsFullScreen(width >= 1024);
      
      // Only allow collapsing if not in full screen mode
      if (width >= 1024) {
        setIsCollapsed(false);
      }
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Set page title based on current route
  useEffect(() => {
    const path = location.pathname;
    let title = 'PMT3';
    
    if (path === '/pmt3' || path === '/pmt3/dashboard') {
      title = 'PMT3';
    } else if (path.startsWith('/pmt3/settings')) {
      title = 'Settings | PMT3';
    } else if (path === '/pmt3/manage-companies') {
      title = 'Manage Companies | PMT3';
    }
    
    setPageTitle(title);
  }, [location.pathname]);

  const isActive = (path: string) => {
    return location.pathname.startsWith(path);
  };

  const { isAdmin } = useIsAdmin();

  const handleNavigate = (to: string) => {
    navigate(to);
    setIsMobileMenuOpen(false);
  };

  // Handle product selection
  const handleProductChange = (value: string) => {
    const product = products.find(p => p.id === value) || null;
    setSelectedProduct(product);
  };

  // Handle ASIN paste input with debounced validation
  const handleAsinPaste = async (value: string) => {
    setPasteAsin(value);
    
    // Clear existing timeout
    if (lookupTimeout) {
      clearTimeout(lookupTimeout);
    }

    if (!value.trim()) {
      setAsinValidationStatus('idle');
      return;
    }

    // Set validating status immediately when user starts typing
    setAsinValidationStatus('validating');

    // Debounce the lookup by 500ms
    const timeoutId = setTimeout(async () => {
      try {
        const result = await pmt2Service.findProductByAsin(value.trim());
        if (result) {
          // ASIN found - auto-populate company and product
          setSelectedCompany(result.company);
          setSelectedProduct(result.product);
          setAsinValidationStatus('valid');
        } else {
          // ASIN not found in any company
          setAsinValidationStatus('invalid');
        }
      } catch (error) {
        console.error('Error looking up ASIN:', error);
        setAsinValidationStatus('invalid');
      }
    }, 500);

    setLookupTimeout(timeoutId);
  };

  // Clear ASIN lookup timeout on unmount
  useEffect(() => {
    return () => {
      if (lookupTimeout) {
        clearTimeout(lookupTimeout);
      }
    };
  }, [lookupTimeout]);

  // Reset ASIN field when company or product changes externally
  useEffect(() => {
    if (selectedProduct && selectedProduct.id !== pasteAsin) {
      setPasteAsin(selectedProduct.id);
      setAsinValidationStatus('valid');
    }
  }, [selectedProduct]);

  // Prepare options for KeyboardNavigableSelect components
  const companyOptions = companies.map(company => ({ value: company, label: company }));
  const productOptions = products.map(product => ({ 
    value: product.id, 
    label: `${product.id}${product.Title ? ` - ${product.Title.substring(0, 30)}${product.Title.length > 30 ? '...' : ''}` : ''}`
  }));

  return (
    <div className="h-screen bg-gray-50 dark:bg-gray-900 flex overflow-hidden pmt2-app pmt3-app">
      {/* Mobile Menu Button */}
      <div className="md:hidden fixed top-0 left-0 right-0 h-16 bg-white dark:bg-gray-800 z-40 border-b dark:border-gray-700 px-4 flex items-center">
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
        >
          {isMobileMenuOpen ? (
            <X className="h-6 w-6 text-gray-600" />
          ) : (
            <Menu className="h-6 w-6 text-gray-600" />
          )}
        </button>
      </div>

      {/* Mobile Menu Backdrop */}
      {isMobileMenuOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 md:hidden"
          onClick={() => setIsMobileMenuOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside 
        className={`
          bg-white dark:bg-gray-800 shadow-sm fixed md:relative h-full z-40
          transform transition-transform duration-300 ease-in-out flex flex-col
          ${isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0 hidden md:flex'}
          ${isCollapsed && !isMobileMenuOpen && !isFullScreen ? 'w-16 md:w-16' : 'w-64 md:w-64'}
        `}
      >
        {/* Collapse Toggle - Hidden on Mobile */}
        <button
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="absolute -right-3 top-6 bg-white dark:bg-gray-800 border dark:border-gray-700 rounded-full p-1 shadow-sm hover:bg-gray-50 dark:hover:bg-gray-700 hidden md:block"
          style={{ display: isFullScreen ? 'none' : undefined }}
        >
          <ChevronLeft className={`h-4 w-4 text-gray-600 transition-transform duration-300 ${isCollapsed ? 'rotate-180' : ''}`} />
        </button>

        <div className="border-b flex justify-center items-center h-46 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700" onClick={() => handleNavigate('/pmt3/dashboard')}>
          {isCollapsed && !isMobileMenuOpen ? (
            <Database className="h-12 w-12 text-orange-500 mb-1 mt-1 transition-all duration-300" />
          ) : (
            <div className="flex flex-col items-center justify-center py-8">
              <Database className="h-20 w-20 text-orange-500 mb-2" />
              <h1 className="text-xl font-bold text-gray-900 dark:text-white">PMT3</h1>
              <p className="text-sm text-gray-500 dark:text-gray-400">Amazon Seller Data Management</p>
            </div>
          )}
        </div>
        
        <nav className="flex-1 mt-4 overflow-y-auto flex flex-col">
          {/* Main Navigation */}
          <div className={`px-4 py-2 mb-4 space-y-2 ${isCollapsed && !isMobileMenuOpen && !isFullScreen ? 'hidden' : 'block'}`}>
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Select Company</label>
              <KeyboardNavigableSelect
                options={companyOptions}
                value={selectedCompany}
                onValueChange={setSelectedCompany}
 
                placeholder="Select a company"
                className="w-full"
                triggerClassName="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                triggerStyle={{ minHeight: '36px' }}
                contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50, paddingLeft: '8px' }}
                itemStyle={{ color: 'black', padding: '8px 12px', paddingLeft: '28px' }}
              />
            </div>
            <div className="mb-2">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Select Product (ASIN)</label>
              <KeyboardNavigableSelect
                options={productOptions}
                value={selectedProduct?.id || ''}
                onValueChange={handleProductChange}
                placeholder="Select a product"
                className="w-full"
                triggerClassName="w-full bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                disabled={!selectedCompany || products.length === 0}
                contentStyle={{ backgroundColor: 'white', color: 'black', zIndex: 50 }}
                itemStyle={{ color: 'black' }}
              />
            </div>

            {/* Paste ASIN Field */}
            <div className="mb-4">
              <label className="block text-sm font-medium mb-1 text-gray-900 dark:text-white">Paste ASIN</label>
              <div className="relative">
                <Input
                  value={pasteAsin}
                  onChange={(e) => handleAsinPaste(e.target.value)}
                  placeholder="Enter or paste ASIN..."
                  className={`w-full ${
                    asinValidationStatus === 'invalid'
                      ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                      : asinValidationStatus === 'valid'
                      ? 'border-green-500 bg-green-50 dark:bg-green-900/20'
                      : 'bg-white dark:bg-gray-700'
                  } text-gray-900 dark:text-white`}
                />
                {asinValidationStatus === 'validating' && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin h-4 w-4 border-2 border-orange-500 border-t-transparent rounded-full"></div>
                  </div>
                )}
              </div>
              <div className="mt-1 text-xs">
                <div className="text-gray-600 dark:text-gray-400">
                  Example: B075W773MZ
                </div>
                {asinValidationStatus === 'invalid' && (
                  <div className="text-red-600 dark:text-red-400">
                    ASIN not found in any company
                  </div>
                )}
                {asinValidationStatus === 'valid' && (
                  <div className="text-green-600 dark:text-green-400">
                    ✓ ASIN found and fields populated
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* ASIN Review Form - Only shown when sidebar is not collapsed */}
          {(!isCollapsed || isMobileMenuOpen) && (
            <>
              {/* Divider */}
              <div className="mx-4 mb-4 border-t border-gray-200 dark:border-gray-700" />
              
              {/* ASIN Review Form */}
              <ASINReviewForm />
            </>
          )}

          {/* Divider */}
          <div className="mx-4 mb-4 border-t border-gray-200 dark:border-gray-700" />

          <div
            onClick={() => handleNavigate('/pmt3/manage-companies')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/pmt3/manage-companies') ? 'bg-orange-50 dark:bg-orange-900/50 text-orange-600 dark:text-orange-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Manage Companies'}
          </div>

          <div
            onClick={() => handleNavigate('/pmt3/dashboard')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/pmt3/dashboard') ? 'bg-orange-50 dark:bg-orange-900/50 text-orange-600 dark:text-orange-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <LayoutGrid className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Dashboard'}
          </div>

          {/* Divider */}
          <div className="mx-4 my-4 border-t border-gray-200 dark:border-gray-700" />

          {/* Utility Navigation */}
          <div
            onClick={() => handleNavigate('/pmt3/settings')}
            className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
              isActive('/pmt3/settings') ? 'bg-orange-50 dark:bg-orange-900/50 text-orange-600 dark:text-orange-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
            }`}
          >
            <Settings className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Settings'}
          </div>

          {/* Admin Link - Only shown to admin users */}
          {isAdmin && (
            <div
              onClick={() => handleNavigate('/pmt3/admin')}
              className={`flex items-center px-4 py-2 text-sm cursor-pointer ${
                isActive('/pmt3/admin') ? 'bg-orange-50 dark:bg-orange-900/50 text-orange-600 dark:text-orange-300' : 'text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800'
              }`}
            >
              <Shield className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
              {(!isCollapsed || isMobileMenuOpen) && 'Admin'}
            </div>
          )}

          {/* Apps Hub Link */}
          <div
            onClick={() => handleNavigate('/dashboard')}
            className="flex items-center px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
          >
            <Home className={`${isCollapsed && !isMobileMenuOpen ? '' : 'mr-3'} h-5 w-5`} />
            {(!isCollapsed || isMobileMenuOpen) && 'Apps Hub'}
          </div>
        </nav>

        {/* User Info Section */}
        <UserInfoSection displayName={displayName} />
      </aside>

      {/* Main Content Area */}
      <main className="flex-1 overflow-auto pt-16 md:pt-0 relative">
        <Outlet />
      </main>
    </div>
  );
};

export default PMT2Layout;